import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, Upload, Trash2, Skull, ShieldCheck, UserPlus, UserMinus, X, Pencil, Save, Lock as LockIcon, Info } from "lucide-react";
import { api } from "@/lib/api";
import { useDiscord } from "@/context/DiscordContext";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { SectionHeader } from "@/components/site/Features";
import KentuckyPin from "@/components/site/KentuckyPin";
import FactionChip from "@/components/site/FactionChip";
import { KENTUCKY_LOCATIONS } from "@/lib/locations";
import { FACTION_COLORS, factionTextColor } from "@/lib/factionColors";

const STATUS_OPTIONS = [
  { v: "alive", l: "Alive" },
  { v: "missing", l: "Missing" },
  { v: "dead", l: "K.I.A." },
];

const initialForm = {
  character_name: "",
  alias: "",
  status: "alive",
  age: "",
  occupation: "",
  origin: "",
  faction: "",
  faction_id: "",
  faction_pending_id: "",
  bio: "",
  last_seen: "",
};

export default function MemberEditPage() {
  const { loaded, isConnected, connect, user } = useDiscord();
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [kiaOpen, setKiaOpen] = useState(false);
  const [killing, setKilling] = useState(false);
  const [isMember, setIsMember] = useState(false);
  const [avatar, setAvatar] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [factions, setFactions] = useState([]);
  const [factionBusy, setFactionBusy] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loaded) return;
    if (!isConnected) {
      setLoading(false);
      return;
    }
    Promise.all([
      api.get("/members/me"),
      api.get("/factions").catch(() => ({ data: { factions: [] } })),
    ])
      .then(([meRes, factionsRes]) => {
        setIsMember(!!meRes.data.is_member);
        setFactions(factionsRes.data.factions || []);
        const m = meRes.data.member || {};
        setAvatar(m.avatar_data_url || null);
        setForm({
          character_name: m.character_name || "",
          alias: m.alias || "",
          status: m.status || "alive",
          age: m.age ?? "",
          occupation: m.occupation || "",
          origin: m.origin || "",
          faction: m.faction || "",
          faction_id: m.faction_id || "",
          faction_pending_id: m.faction_pending_id || "",
          bio: m.bio || "",
          last_seen: m.last_seen || "",
        });
      })
      .catch(() => setIsMember(false))
      .finally(() => setLoading(false));
  }, [loaded, isConnected]);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onAvatarFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Pick an image file.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image must be 2 MB or smaller.");
      return;
    }
    setUploadingAvatar(true);
    try {
      const dataUrl = await resizeToSquare(file, 300);
      await api.put("/members/me/avatar", { image_data_url: dataUrl });
      setAvatar(dataUrl);
      toast.success("Avatar updated.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Upload failed.");
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeAvatar = async () => {
    if (!window.confirm("Remove your survivor portrait?")) return;
    try {
      await api.delete("/members/me/avatar");
      setAvatar(null);
      toast.success("Avatar removed.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remove failed.");
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.character_name.trim()) {
      toast.error("Character name is required.");
      return;
    }
    // K.I.A. requires the user to acknowledge a destructive warning before we
    // archive the character. We re-route the save through the dedicated endpoint.
    if (form.status === "dead") {
      setKiaOpen(true);
      return;
    }
    setSaving(true);
    try {
      await api.put("/members/me", {
        character_name: form.character_name.trim(),
        alias: form.alias.trim() || null,
        status: form.status,
        age: form.age === "" ? null : Number(form.age),
        occupation: form.occupation.trim() || null,
        origin: form.origin.trim() || null,
        // `faction` is server-managed via the request/accept flow — don't send it here.
        bio: form.bio.trim(),
        last_seen: form.last_seen.trim() || null,
      });
      toast.success("Profile saved.");
      navigate("/members");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const confirmKia = async () => {
    setKilling(true);
    try {
      await api.post("/members/me/kia");
      toast.success("Character archived to the Graveyard. Time to write a new story.");
      // Reset form locally to the blank slate the backend just created.
      setForm(initialForm);
      setAvatar(null);
      setKiaOpen(false);
      // Stay on the edit page so they can fill in the new character right away.
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to archive character.");
    } finally {
      setKilling(false);
    }
  };

  const cancelKia = () => {
    if (killing) return;
    setKiaOpen(false);
    // Revert the status select back to Alive so a stray Save doesn't re-trigger the modal.
    setForm((p) => ({ ...p, status: "alive" }));
  };

  return (
    <div className="App min-h-screen">
      <Navbar />
      <main className="pt-[72px]">
        {user?.led_faction_id && (
          <FactionLeaderPanel
            factionId={user.led_faction_id}
            factionName={user.led_faction_name}
          />
        )}
        <section className="py-20 sm:py-24 border-t border-[#283524]">
          <div className="max-w-3xl mx-auto px-6">
            <SectionHeader
              eyebrow="// SURVIVOR PROFILE"
              title="Tell us who you are."
              desc="This shows on the public Survivors wall. Keep it in character — your story is part of the world now."
            />

            {!loaded || loading ? (
              <div className="mt-16 text-center py-16">
                <Loader2 className="animate-spin text-[#C0A875] mx-auto" size={24} />
              </div>
            ) : !isConnected ? (
              <LockedCard
                title="Connect Discord to edit your profile"
                body="Only approved survivors can edit a profile on the Survivors wall."
                action={
                  <button
                    onClick={connect}
                    data-testid="member-connect-button"
                    className="bg-[#5865F2] hover:bg-[#4752C4] text-white px-6 py-3 font-mono-ui text-xs uppercase tracking-[0.25em]"
                  >
                    Connect Discord
                  </button>
                }
              />
            ) : !isMember ? (
              <LockedCard
                title="You're not on the wall yet."
                body="The Survivors profile is only available after your whitelist application has been approved. Submit one if you haven't already."
                action={
                  <Link
                    to="/apply"
                    className="border border-[#C0A875] text-[#C0A875] hover:bg-[#C0A875]/15 px-6 py-3 font-mono-ui text-xs uppercase tracking-[0.25em]"
                  >
                    Go to application →
                  </Link>
                }
              />
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="member-edit-form"
                className="mt-12 bg-[#161D14] border border-[#283524] p-6 sm:p-10 space-y-8"
              >
                {/* Avatar */}
                <div className="flex flex-col sm:flex-row gap-6 sm:items-center pb-6 border-b border-[#283524]">
                  <div className="relative w-32 h-32 bg-[#0D110C] border border-[#283524] flex items-center justify-center overflow-hidden shrink-0">
                    <div className="absolute inset-0 tactical-grid opacity-50" />
                    {avatar ? (
                      <img
                        src={avatar}
                        alt="Your survivor"
                        className="absolute inset-0 w-full h-full object-cover"
                        data-testid="avatar-preview"
                      />
                    ) : (
                      <span className="relative font-display text-5xl text-[#C0A875] uppercase">
                        {(form.character_name || "?")[0]}
                      </span>
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display uppercase text-[#E2E4DF] text-xl">Survivor portrait</h3>
                    <p className="mt-1 text-[13px] text-[#A8B5A0] leading-relaxed">
                      Square image, auto-resized to 300×300. JPEG/PNG, 2 MB max. Replaces any existing portrait.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-3">
                      <input
                        type="file"
                        accept="image/*"
                        ref={fileInputRef}
                        onChange={onAvatarFileSelected}
                        className="hidden"
                        data-testid="avatar-file-input"
                      />
                      <button
                        type="button"
                        data-testid="avatar-upload-button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingAvatar}
                        className="inline-flex items-center gap-2 border border-[#C0A875] text-[#C0A875] hover:bg-[#C0A875]/15 px-4 py-2 font-mono-ui text-[11px] uppercase tracking-[0.2em] disabled:opacity-60"
                      >
                        {uploadingAvatar ? <Loader2 className="animate-spin" size={12} /> : <Upload size={12} />}
                        {avatar ? "Replace portrait" : "Upload portrait"}
                      </button>
                      {avatar && (
                        <button
                          type="button"
                          data-testid="avatar-remove-button"
                          onClick={removeAvatar}
                          className="inline-flex items-center gap-2 border border-[#C0392B] text-[#C0392B] hover:bg-[#C0392B]/15 px-4 py-2 font-mono-ui text-[11px] uppercase tracking-[0.2em]"
                        >
                          <Trash2 size={12} /> Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <Row>
                  <FieldLabel label="Character name" required />
                  <Input
                    data-testid="profile-name"
                    value={form.character_name}
                    onChange={(e) => update("character_name", e.target.value)}
                    required
                    maxLength={48}
                  />
                </Row>
                <Row>
                  <FieldLabel label="Alias / callsign" />
                  <Input
                    data-testid="profile-alias"
                    value={form.alias}
                    onChange={(e) => update("alias", e.target.value)}
                    maxLength={48}
                    placeholder="e.g. The Tinkerer, Ghost"
                  />
                </Row>
                <Grid>
                  <Row>
                    <FieldLabel label="Status" required />
                    <Select
                      data-testid="profile-status"
                      value={form.status}
                      onChange={(e) => update("status", e.target.value)}
                    >
                      {STATUS_OPTIONS.map((o) => (
                        <option key={o.v} value={o.v}>{o.l}</option>
                      ))}
                    </Select>
                  </Row>
                  <Row>
                    <FieldLabel label="Age (in-character)" />
                    <Input
                      data-testid="profile-age"
                      type="number"
                      min={0}
                      max={120}
                      value={form.age}
                      onChange={(e) => update("age", e.target.value)}
                    />
                  </Row>
                </Grid>
                <Grid>
                  <Row>
                    <FieldLabel label="Origin" />
                    <Input
                      data-testid="profile-origin"
                      value={form.origin}
                      onChange={(e) => update("origin", e.target.value)}
                      maxLength={64}
                      placeholder="Where they're from"
                    />
                  </Row>
                  <Row>
                    <FieldLabel label="Occupation" />
                    <Input
                      data-testid="profile-occupation"
                      value={form.occupation}
                      onChange={(e) => update("occupation", e.target.value)}
                      maxLength={64}
                      placeholder="Mechanic, medic, scavenger…"
                    />
                  </Row>
                </Grid>
                <Row>
                  <FieldLabel label="Faction / allegiance" />
                  <FactionPicker
                    factions={factions}
                    currentId={form.faction_id}
                    currentName={form.faction}
                    pendingId={form.faction_pending_id}
                    busy={factionBusy}
                    onRequest={async (id) => {
                      setFactionBusy(true);
                      try {
                        const res = await api.post("/members/me/faction-request", { faction_id: id || null });
                        if (res.data.status === "pending") {
                          const f = factions.find((x) => x.id === id);
                          toast.success(`Request sent to ${f?.name || "the faction leader"}. Awaiting acceptance.`);
                          setForm((p) => ({ ...p, faction_pending_id: id }));
                        } else if (res.data.status === "left") {
                          toast.success("You left the faction.");
                          setForm((p) => ({ ...p, faction: "", faction_id: "", faction_pending_id: "" }));
                        }
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Couldn't update faction.");
                      } finally {
                        setFactionBusy(false);
                      }
                    }}
                  />
                </Row>
                <Row>
                  <FieldLabel label="Public bio" />
                  <p className="text-[13px] text-[#A8B5A0] -mt-1 mb-2 leading-relaxed">
                    What other survivors would know or whisper about your character. In-character voice recommended.
                  </p>
                  <Textarea
                    data-testid="profile-bio"
                    rows={6}
                    value={form.bio}
                    onChange={(e) => update("bio", e.target.value)}
                    maxLength={1500}
                  />
                </Row>
                <Row>
                  <FieldLabel label="Last seen" />
                  <Select
                    data-testid="profile-last-seen"
                    value={form.last_seen}
                    onChange={(e) => update("last_seen", e.target.value)}
                  >
                    <option value="">— Unknown / off-grid —</option>
                    {KENTUCKY_LOCATIONS.map((loc) => (
                      <option key={loc} value={loc}>
                        {loc}
                      </option>
                    ))}
                    {/* Preserve any legacy free-text value so we don't lose it on save. */}
                    {form.last_seen && !KENTUCKY_LOCATIONS.includes(form.last_seen) && (
                      <option value={form.last_seen}>{form.last_seen} (legacy)</option>
                    )}
                  </Select>
                </Row>

                <div className="pt-4 border-t border-[#283524] flex justify-end gap-3">
                  <Link
                    to="/members"
                    className="border border-[#5C6B51] hover:border-[#C0A875] text-[#E2E4DF] hover:text-[#C0A875] px-5 py-2.5 font-mono-ui text-xs uppercase tracking-[0.25em]"
                  >
                    Cancel
                  </Link>
                  <button
                    type="submit"
                    data-testid="profile-save"
                    disabled={saving}
                    className="bg-[#C0A875] hover:bg-[#a99362] disabled:bg-[#283524] disabled:text-[#8D9A87] text-[#0D110C] px-7 py-2.5 font-mono-ui text-xs uppercase tracking-[0.25em] font-semibold inline-flex items-center gap-2"
                  >
                    {saving && <Loader2 className="animate-spin" size={14} />}
                    Save profile →
                  </button>
                </div>
              </form>
            )}
          </div>
        </section>
      </main>
      <Footer />
      {kiaOpen && (
        <KiaConfirmModal
          characterName={form.character_name}
          loading={killing}
          onConfirm={confirmKia}
          onClose={cancelKia}
        />
      )}
    </div>
  );
}

function KiaConfirmModal({ characterName, loading, onConfirm, onClose }) {
  return (
    <div
      data-testid="kia-modal"
      className="fixed inset-0 z-50 bg-[#0D110C]/85 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
      onClick={onClose}
    >
      <div
        className="relative bg-[#161D14] border-2 border-[#C0392B] max-w-lg w-full p-8 sm:p-10 shadow-[0_0_60px_rgba(192,57,43,0.25)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3">
          <Skull className="text-[#C0392B]" size={28} />
          <h2 className="font-display uppercase text-[#C0392B] text-2xl sm:text-3xl tracking-tight">
            ⚠ Warning — K.I.A.
          </h2>
        </div>

        <div className="mt-5 p-4 border border-[#C0392B]/60 bg-[#C0392B]/10">
          <p className="font-mono-ui text-[12px] uppercase tracking-[0.15em] text-[#C0392B] font-semibold">
            // This cannot be undone by you.
          </p>
          <p className="mt-3 text-[#E2E4DF] text-sm leading-relaxed">
            You're about to mark{" "}
            <span className="text-[#C0A875] font-semibold">
              {characterName || "this character"}
            </span>{" "}
            as <span className="text-[#C0392B] font-bold uppercase">Killed In Action</span>.
            They will be moved to the public Graveyard wall — permanently.
          </p>
          <p className="mt-3 text-[#E2E4DF] text-sm leading-relaxed">
            Your profile slot will be <span className="text-[#C0392B] font-semibold">wiped clean</span>.
            You'll need to <span className="text-[#C0A875] font-semibold">fill out a new character name, bio, age, origin and the rest</span> for your next survivor before you can save again.
          </p>
          <p className="mt-3 text-[#8D9A87] text-xs leading-relaxed font-mono-ui">
            // Only <span className="text-[#C0A875]">@occifercody</span> can revive a K.I.A. character. You cannot bring them back yourself.
          </p>
        </div>

        <div className="mt-8 flex justify-end gap-3">
          <button
            type="button"
            data-testid="kia-cancel"
            onClick={onClose}
            disabled={loading}
            className="border border-[#5C6B51] hover:border-[#C0A875] text-[#E2E4DF] hover:text-[#C0A875] px-5 py-2.5 font-mono-ui text-xs uppercase tracking-[0.25em] disabled:opacity-50"
          >
            Cancel — keep them alive
          </button>
          <button
            type="button"
            data-testid="kia-confirm"
            onClick={onConfirm}
            disabled={loading}
            className="bg-[#C0392B] hover:bg-[#a23022] text-[#E2E4DF] disabled:opacity-60 px-6 py-2.5 font-mono-ui text-xs uppercase tracking-[0.25em] font-semibold inline-flex items-center gap-2"
          >
            {loading && <Loader2 className="animate-spin" size={14} />}
            <Skull size={14} />
            Yes, mark K.I.A.
          </button>
        </div>
      </div>
    </div>
  );
}

function LockedCard({ title, body, action }) {
  return (
    <div className="mt-16 bg-[#161D14] border border-[#283524] p-10 text-center">
      <Lock className="text-[#C0A875] mx-auto" size={32} />
      <h3 className="mt-4 font-display uppercase text-[#E2E4DF] text-2xl sm:text-3xl">{title}</h3>
      <p className="mt-3 text-[#8D9A87] text-sm max-w-md mx-auto">{body}</p>
      <div className="mt-6 flex justify-center">{action}</div>
    </div>
  );
}

function Row({ children }) {
  return <div className="space-y-2">{children}</div>;
}
function Grid({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">{children}</div>;
}
function FieldLabel({ label, required }) {
  return (
    <span className="block font-mono-ui text-xs tracking-[0.18em] uppercase text-[#E2E4DF] font-bold">
      {label} {required && <span className="text-[#C0A875] font-black text-base align-middle ml-0.5">*</span>}
    </span>
  );
}
const inputClasses =
  "w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-4 py-3 font-mono-ui text-sm focus:border-[#C0A875] focus:outline-none transition-colors placeholder:text-[#8D9A87]/50";
function Input(props) {
  return <input {...props} className={inputClasses} />;
}
function Textarea(props) {
  return <textarea {...props} className={`${inputClasses} resize-y leading-relaxed`} />;
}
function Select({ children, ...props }) {
  return <select {...props} className={inputClasses}>{children}</select>;
}

/**
 * Reads a File, resizes it to fit within `size`x`size` (center-cropped to square),
 * and returns a JPEG data URL. Quality stepped down if the result is too large.
 */
async function resizeToSquare(file, size = 300) {
  const dataUrl = await readAsDataUrl(file);
  const img = await loadImage(dataUrl);
  // Center-crop to a square first
  const srcSize = Math.min(img.naturalWidth, img.naturalHeight);
  const sx = (img.naturalWidth - srcSize) / 2;
  const sy = (img.naturalHeight - srcSize) / 2;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, sx, sy, srcSize, srcSize, 0, 0, size, size);

  // Step quality down if needed to stay under the server cap (~500 KB data URL)
  let quality = 0.88;
  let out = canvas.toDataURL("image/jpeg", quality);
  while (out.length > 480_000 && quality > 0.4) {
    quality -= 0.1;
    out = canvas.toDataURL("image/jpeg", quality);
  }
  return out;
}

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error("Could not read file."));
    r.readAsDataURL(file);
  });
}
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not load image."));
    img.src = src;
  });
}

function FactionPicker({ factions, currentId, currentName, pendingId, busy, onRequest }) {
  const [selected, setSelected] = useState(pendingId || currentId || "");
  useEffect(() => {
    setSelected(pendingId || currentId || "");
  }, [pendingId, currentId]);

  const pendingFaction = factions.find((f) => f.id === pendingId);
  const currentFaction = factions.find((f) => f.id === currentId);

  return (
    <div className="space-y-3">
      {currentFaction && (
        <div className="inline-flex items-center gap-3" data-testid="faction-current">
          <FactionChip faction={currentFaction} size="md" />
          <span className="font-mono-ui text-[10px] uppercase tracking-[0.2em] text-[#9CC279]">
            Currently in
          </span>
        </div>
      )}
      {pendingFaction && (
        <div className="inline-flex items-center gap-3" data-testid="faction-pending">
          <FactionChip faction={pendingFaction} size="md" />
          <span className="font-mono-ui text-[10px] uppercase tracking-[0.2em] text-[#C0A875] inline-flex items-center gap-2">
            <Loader2 className="animate-spin" size={10} /> Awaiting acceptance
          </span>
        </div>
      )}

      <Select
        data-testid="profile-faction"
        value={selected}
        onChange={(e) => setSelected(e.target.value)}
        disabled={busy}
      >
        <option value="">— No faction / unaligned —</option>
        {factions.map((f) => (
          <option key={f.id} value={f.id}>
            {f.name}
            {f.leader_name ? ` · led by ${f.leader_name}` : ""}
          </option>
        ))}
        {/* Preserve any free-text legacy faction so renaming doesn't wipe it visually */}
        {!currentId && currentName && (
          <option value="" disabled>
            (legacy: {currentName})
          </option>
        )}
      </Select>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          data-testid="faction-request-button"
          onClick={() => onRequest(selected || null)}
          disabled={busy || selected === (pendingId || currentId || "")}
          className="border border-[#C0A875] text-[#C0A875] hover:bg-[#C0A875]/15 disabled:opacity-40 disabled:cursor-not-allowed px-4 py-2 font-mono-ui text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-2"
        >
          <UserPlus size={12} />
          {selected ? "Request to join" : "Leave faction"}
        </button>
        {pendingId && (
          <button
            type="button"
            data-testid="faction-cancel-pending"
            onClick={() => onRequest(null)}
            disabled={busy}
            className="border border-[#5C6B51] text-[#8D9A87] hover:text-[#E2E4DF] hover:border-[#C0A875] px-4 py-2 font-mono-ui text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-2"
          >
            <X size={12} /> Cancel pending
          </button>
        )}
      </div>

      <p className="font-mono-ui text-[10px] text-[#8D9A87] leading-relaxed">
        // The faction leader must accept your request before you officially join.
        // You can leave a faction at any time without approval.
      </p>
    </div>
  );
}


function FactionLeaderPanel({ factionId, factionName }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/factions/${factionId}`);
      setDetail(res.data || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load faction.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [factionId]);

  const accept = async (id) => {
    try {
      await api.post(`/factions/${factionId}/accept/${id}`);
      toast.success("Accepted into the faction.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Accept failed.");
    }
  };

  const reject = async (id) => {
    try {
      await api.post(`/factions/${factionId}/reject/${id}`);
      toast.success("Request rejected.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reject failed.");
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Remove ${name || "this member"} from the faction?`)) return;
    try {
      await api.post(`/factions/${factionId}/remove/${id}`);
      toast.success("Member removed.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remove failed.");
    }
  };

  return (
    <section className="py-12 sm:py-14 bg-[#0F1410] border-b border-[#283524]" data-testid="faction-leader-panel">
      <div className="max-w-3xl mx-auto px-6">
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="text-[#C0A875]" size={20} />
          <span className="font-mono-ui text-xs uppercase tracking-[0.25em] text-[#C0A875]">
            Faction Leader
          </span>
        </div>
        <h2 className="font-display uppercase text-[#E2E4DF] text-2xl sm:text-3xl tracking-tight">
          {factionName}
        </h2>
        <p className="mt-2 text-[#8D9A87] text-sm">
          Accept survivors who want to join, or remove members who shouldn't be on the roster anymore.
        </p>

        {loading ? (
          <div className="py-12 text-center">
            <Loader2 className="animate-spin text-[#C0A875] mx-auto" size={20} />
          </div>
        ) : (
          <div className="mt-8 space-y-10">
            <FactionAppearanceEditor
              factionId={factionId}
              initial={detail}
              onSaved={(f) => setDetail((d) => ({ ...d, ...f }))}
            />

            <FactionLeaderList
              title="Pending requests"
              empty="// No one is knocking right now."
              rows={detail?.pending || []}
              actions={(m) => (
                <div className="flex gap-2">
                  <button
                    data-testid={`faction-accept-${m.discord_id}`}
                    onClick={() => accept(m.discord_id)}
                    className="border border-[#9CC279] text-[#9CC279] hover:bg-[#9CC279]/15 px-3 py-1.5 font-mono-ui text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-1.5"
                  >
                    <UserPlus size={11} /> Accept
                  </button>
                  <button
                    data-testid={`faction-reject-${m.discord_id}`}
                    onClick={() => reject(m.discord_id)}
                    className="border border-[#C0392B] text-[#C0392B] hover:bg-[#C0392B]/15 px-3 py-1.5 font-mono-ui text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-1.5"
                  >
                    <X size={11} /> Reject
                  </button>
                </div>
              )}
            />

            <FactionLeaderList
              title="Roster"
              empty="// Just you for now, leader."
              rows={(detail?.members || []).filter((m) => m.discord_id !== detail?.leader_discord_id)}
              actions={(m) => (
                <button
                  data-testid={`faction-remove-${m.discord_id}`}
                  onClick={() => remove(m.discord_id, m.character_name)}
                  className="border border-[#5C6B51] text-[#8D9A87] hover:text-[#C0392B] hover:border-[#C0392B] px-3 py-1.5 font-mono-ui text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-1.5"
                >
                  <UserMinus size={11} /> Remove
                </button>
              )}
            />
          </div>
        )}
      </div>
    </section>
  );
}

function FactionLeaderList({ title, empty, rows, actions }) {
  return (
    <div>
      <h3 className="font-mono-ui text-[11px] uppercase tracking-[0.25em] text-[#8D9A87] mb-3">
        {title} <span className="text-[#5C6B51]">({rows.length})</span>
      </h3>
      {rows.length === 0 ? (
        <p className="font-mono-ui text-xs text-[#5C6B51]">{empty}</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((m) => (
            <li
              key={m.discord_id}
              className="flex items-center gap-3 p-3 bg-[#161D14] border border-[#283524]"
            >
              <div className="w-9 h-9 border border-[#283524] flex items-center justify-center overflow-hidden shrink-0">
                {m.avatar_data_url ? (
                  <img src={m.avatar_data_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="font-display text-[#C0A875] text-sm">
                    {(m.character_name || m.discord_username || "?")[0]}
                  </span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display uppercase text-[#E2E4DF] text-sm truncate">
                  {m.character_name || m.discord_username || "Unnamed"}
                </div>
                <div className="font-mono-ui text-[10px] text-[#8D9A87] truncate">
                  @{m.discord_username || "—"}
                </div>
              </div>
              {actions(m)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


/**
 * Lets the faction leader tweak cosmetic / narrative fields of their faction:
 * colour and description. Name + acronym are intentionally read-only — those
 * changes go through a Discord ticket to keep history clean.
 */
function FactionAppearanceEditor({ factionId, initial, onSaved }) {
  const [open, setOpen] = useState(false);
  const [color, setColor] = useState(initial?.color || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setColor(initial?.color || "");
    setDescription(initial?.description || "");
  }, [initial?.color, initial?.description, factionId]);

  const dirty =
    (color || "") !== (initial?.color || "") ||
    (description || "") !== (initial?.description || "");

  const reset = () => {
    setColor(initial?.color || "");
    setDescription(initial?.description || "");
  };

  const save = async () => {
    const trimmed = description.trim();
    if (trimmed && trimmed.length < 25) {
      toast.error(`Description needs at least 25 characters (you have ${trimmed.length}).`);
      return;
    }
    setSaving(true);
    try {
      const res = await api.patch(`/factions/${factionId}/leader-update`, {
        color,
        description: trimmed,
      });
      toast.success("Faction updated.");
      onSaved?.(res.data?.faction || { color, description: trimmed });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed.");
    } finally {
      setSaving(false);
    }
  };

  const previewChip = {
    name: initial?.name,
    acronym: initial?.acronym,
    color: color || initial?.color,
  };

  return (
    <div
      data-testid="faction-appearance-editor"
      className="bg-[#161D14] border border-[#283524]"
    >
      <button
        type="button"
        data-testid="faction-edit-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-[#1B221A] transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          <Pencil className="text-[#C0A875] shrink-0" size={14} />
          <span className="font-mono-ui text-xs uppercase tracking-[0.25em] text-[#E2E4DF]">
            Edit Colour & Description
          </span>
          <FactionChip faction={previewChip} size="sm" testid="faction-edit-preview" />
        </div>
        <span className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87]">
          {open ? "Hide" : "Open"} →
        </span>
      </button>

      {open && (
        <div className="px-5 sm:px-6 pb-6 pt-2 space-y-6 border-t border-[#283524]">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <LockedField label="Faction name" value={initial?.name} testid="faction-locked-name" />
            <LockedField label="Acronym" value={initial?.acronym} testid="faction-locked-acronym" />
          </div>
          <p className="flex items-start gap-2 font-mono-ui text-[10px] uppercase tracking-[0.2em] text-[#8D9A87]">
            <Info size={12} className="mt-[1px] text-[#C0A875] shrink-0" />
            <span>
              // Name &amp; acronym changes must be requested via a{" "}
              <span className="text-[#C0A875]">Discord ticket</span>.
            </span>
          </p>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87]">
                Colour <span className="text-[#C0A875]">*</span>
              </span>
              {color && (
                <span className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87]">
                  Selected: <span className="text-[#E2E4DF]">{color}</span>
                </span>
              )}
            </div>
            <div
              data-testid="faction-edit-color-grid"
              className="grid gap-1.5 p-2 bg-[#0D110C] border border-[#283524]"
              style={{ gridTemplateColumns: "repeat(auto-fill, minmax(28px, 1fr))" }}
            >
              {FACTION_COLORS.map((c) => {
                const selected = (color || "").toLowerCase() === c.toLowerCase();
                return (
                  <button
                    type="button"
                    key={c}
                    title={c}
                    data-testid={`faction-edit-color-${c.toLowerCase()}`}
                    onClick={() => setColor(c)}
                    style={{ background: c }}
                    className={`h-7 w-full border-2 transition-all ${
                      selected
                        ? "border-[#E2E4DF] scale-110 z-10 relative shadow-[0_0_0_2px_#0D110C]"
                        : "border-[#0D110C] hover:border-[#C0A875]"
                    }`}
                    aria-label={c}
                  >
                    {selected && (
                      <span
                        aria-hidden
                        className="block w-full h-full"
                        style={{
                          color: factionTextColor(c),
                          textAlign: "center",
                          lineHeight: "100%",
                          fontWeight: 900,
                          fontSize: "11px",
                        }}
                      >
                        ✓
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87]">
                Description
              </span>
              <span className="font-mono-ui text-[10px] uppercase tracking-[0.2em] text-[#5C6B51]">
                {description.length} CHARS · MIN 25
              </span>
            </div>
            <textarea
              data-testid="faction-edit-description"
              rows={5}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={1000}
              placeholder="What does this faction stand for?"
              className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-xs focus:border-[#C0A875] focus:outline-none resize-y"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-[#283524]">
            <button
              type="button"
              data-testid="faction-edit-save"
              onClick={save}
              disabled={!dirty || saving}
              className="inline-flex items-center gap-2 bg-[#C0A875] hover:bg-[#a99362] disabled:bg-[#283524] disabled:text-[#8D9A87] text-[#0D110C] px-5 py-2 font-mono-ui text-[11px] uppercase tracking-[0.25em] font-semibold transition-colors"
            >
              {saving ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />}
              {saving ? "Saving…" : "Save changes"}
            </button>
            {dirty && !saving && (
              <button
                type="button"
                data-testid="faction-edit-reset"
                onClick={reset}
                className="font-mono-ui text-[11px] uppercase tracking-[0.25em] text-[#8D9A87] hover:text-[#E2E4DF]"
              >
                Discard
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function LockedField({ label, value, testid }) {
  return (
    <div className="bg-[#0D110C] border border-[#283524] px-3 py-2.5" data-testid={testid}>
      <div className="flex items-center gap-1.5">
        <LockIcon className="text-[#5C6B51]" size={10} />
        <span className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#5C6B51]">
          {label}
        </span>
      </div>
      <p className="mt-1 font-display uppercase text-[#8D9A87] text-sm tracking-tight truncate">
        {value || "—"}
      </p>
    </div>
  );
}
